import { Transition } from '@headlessui/react';
import { useEffect, useRef, useState } from 'react';
import { useClickOutside } from '@hooks/useClickOutside';
import NotificationList from '@layouts/components/Header/components/NotificationPanel/NotificationList';
import {
  NotificationContextProvider,
  useNotificationContext
} from '@layouts/components/Header/components/NotificationPanel/context';
import Modal from '@ui/Modal';
import {
  Button,
  ButtonVariants,
  Modes
} from 'stubben-edge-unified-frontend-react-components';
import IconCloseCircle from '@icons/IconCloseCircle';
import IconWarning from '@icons/IconWarning';
import {
  BellIcon,
  IconButton
} from 'stubben-edge-unified-frontend-react-components';

const poll = 30000;

const NotificationPanelInner = () => {
  const {
    count,
    fetchCount,
    fetchData,
    markForDelete,
    setMarkForDelete,
    handleDeleteNotification,
    working
  } = useNotificationContext();

  const [panelOpen, setPanelOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const togglePanel = () => {
    setPanelOpen(!panelOpen);
  };

  useClickOutside([containerRef], () => {
    if (panelOpen) setPanelOpen(false);
  });

  const handleDelete = () => {
    if (markForDelete) handleDeleteNotification(markForDelete);
  };

  const handleCloseDelete = () => {
    setMarkForDelete(null);
  };

  /**
   * Fetch count every 30 seconds
   */
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    fetchCount();
    intervalRef.current = setInterval(() => {
      fetchCount();
      if (count >= 1) fetchData(1);
    }, poll);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  /**
   * Refresh list every time the panel is opened
   */
  useEffect(() => {
    if (panelOpen) {
      fetchData(1);
      fetchCount();
    }
  }, [panelOpen]);

  return (
    <span className="relative inline-flex" ref={containerRef}>
      <IconButton
        onClick={togglePanel}
        mode="ghost"
        testIdentifier="header-notifications-button"
      >
        <BellIcon size={32} />
      </IconButton>
      {count > 0 && (
        <div
          id="header-notifications-badge"
          name="header-notifications-badge"
          data-testid="header-notifications-badge"
          className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-error p-1 text-2xs font-semibold text-white"
        >
          {count > 99 ? 99 : count}
        </div>
      )}
      <Transition
        show={panelOpen}
        enter="transition duration-300 ease-out"
        enterFrom="opacity-0 scale-0"
        enterTo="opacity-100 scale-100"
        leave="transition duration-200"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-0"
        className="absolute -right-4 top-12 z-dropdown w-[438px] origin-top-right"
      >
        <NotificationList />
      </Transition>
      <Modal
        open={markForDelete !== null}
        onClose={handleCloseDelete}
        title="Delete notification"
        headerIcon={<IconWarning className="text-warning" />}
        actions={
          <div className="flex w-full flex-nowrap justify-between space-x-2">
            <Button
              variant={ButtonVariants.Tertiary}
              mode={Modes.Ghost}
              leftSlot={<IconCloseCircle />}
              onClick={handleCloseDelete}
              testIdentifier="header-notifications-delete-cancel"
            >
              Cancel
            </Button>
            <Button
              mode={Modes.Warning}
              loading={working === markForDelete}
              onClick={handleDelete}
              testIdentifier="header-notifications-delete-confirm"
            >
              Delete notification
            </Button>
          </div>
        }
      >
        <p className="mb-5 text-lg font-bold text-[#2D3047]">
          Are you sure that you want to delete this notification?
        </p>
      </Modal>
    </span>
  );
};

const NotificationPanel = () => {
  return (
    <NotificationContextProvider>
      <NotificationPanelInner />
    </NotificationContextProvider>
  );
};

export default NotificationPanel;
